
import { useQuery } from '@tanstack/react-query';
import { useUser } from '@/contexts/UserContext';
import { supabase } from '@/integrations/supabase/client';
import { handleServiceError } from '@/services/utils/errorHandling';
import { CURRENT_USER_FAMILY, getEntityKey } from '@/constants/queryKeys';

/**
 * Hook to fetch the current user's family information
 * Uses current_family_id field for consistency
 */
export const useCurrentUserFamily = () => {
  const { user } = useUser();
  
  const {
    data,
    isLoading,
    error
  } = useQuery({
    queryKey: getEntityKey(CURRENT_USER_FAMILY, user?.id),
    queryFn: async () => {
      try {
        if (!user) {
          throw new Error("No authenticated user found");
        }

        // Get the user's profile
        const { data: profileData, error: profileError } = await supabase
          .from("profiles")
          .select("current_family_id, registration_family_id")
          .eq("id", user.id)
          .maybeSingle();

        if (profileError) throw profileError;
        
        // If no current family, we can't fetch family information
        if (!profileData?.current_family_id) {
          return {
            hasFamily: false,
            familyId: null,
            isAdmin: profileData?.registration_family_id === profileData?.current_family_id
          };
        }
        
        return {
          hasFamily: !!profileData.current_family_id,
          familyId: profileData.current_family_id || null,
          isAdmin: profileData.registration_family_id === profileData.current_family_id
        };
      } catch (error) {
        handleServiceError(error, "Error fetching user family information");
        throw error;
      }
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    hasFamily: data?.hasFamily || false,
    familyId: data?.familyId || null,
    isAdmin: data?.isAdmin || false,
    isLoading,
    error: error ? String(error) : null
  };
};
